import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { chatAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { fmtTime, fmtDate } from '../utils/dateUtils';
import { parseApiError } from '../utils/errors';

const POLL_MS = 5000;

/** Read receipts — click to expand who has seen the message and when. */
function SeenBy({ seen }) {
  const [open, setOpen] = useState(false);
  if (!seen || seen.length === 0) {
    return <span className="chat-seen-none">Not seen yet</span>;
  }
  return (
    <span className="chat-seen">
      <button type="button" className="chat-seen-btn" onClick={() => setOpen(!open)}
        title="Who has seen this message">
        👁 Seen by {seen.length}
      </button>
      {open && (
        <div className="chat-seen-list">
          {seen.map((s) => (
            <div key={s.user_id}>{s.full_name} · {fmtTime(s.seen_at)}</div>
          ))}
        </div>
      )}
    </span>
  );
}

/**
 * Attachments are behind auth, so a plain <img src> would 401. Fetch the blob
 * with the API client and render it from an object URL.
 */
function Attachment({ m }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objUrl;
    chatAPI.downloadFile(m.id)
      .then(({ data }) => {
        objUrl = URL.createObjectURL(data);
        if (!cancelled) setUrl(objUrl); else URL.revokeObjectURL(objUrl);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [m.id]);

  if (failed) return <div className="text-muted" style={{ fontSize: 12 }}>Attachment unavailable</div>;
  if (!url) return <div className="text-muted" style={{ fontSize: 12 }}>Loading attachment…</div>;

  if (m.type === 'image') {
    return <a href={url} target="_blank" rel="noreferrer"><img className="chat-image" src={url} alt={m.original_filename || 'image'} /></a>;
  }
  if (m.type === 'voice') {
    return <audio controls src={url} style={{ maxWidth: '100%' }} />;
  }
  return (
    <a className="chat-file" href={url} download={m.original_filename || 'file'}>
      📎 {m.original_filename}{m.file_size ? ` (${(m.file_size / 1024).toFixed(0)} KB)` : ''}
    </a>
  );
}

export default function Chat() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'super_admin';

  const { roomId } = useParams();
  const navigate = useNavigate();
  const activeId = roomId ? Number(roomId) : null;

  const [rooms, setRooms] = useState([]);
  const [messages, setMessages] = useState([]);
  const [people, setPeople] = useState([]);
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [groupMembers, setGroupMembers] = useState([]);
  const [recording, setRecording] = useState(false);
  const [peopleSearch, setPeopleSearch] = useState('');

  const fileRef = useRef();
  const bottomRef = useRef();
  const recorderRef = useRef(null);
  const activeIdRef = useRef(null);
  activeIdRef.current = activeId;

  const openRoom = useCallback((id) => navigate(`/chat/${id}`), [navigate]);

  const loadRooms = useCallback(async () => {
    try {
      const { data } = await chatAPI.rooms();
      setRooms(data);
      return data;
    } catch (e) { return []; }
  }, []);

  const loadMessages = useCallback(async (roomId) => {
    if (!roomId) return;
    try {
      const { data } = await chatAPI.messages(roomId, { limit: 200 });
      setMessages(data);
    } catch (e) { /* ignore poll errors */ }
  }, []);

  useEffect(() => {
    loadRooms().then((data) => {
      // Default to the first room (All Staff) when no room is in the URL
      if (!roomId && data && data.length) navigate(`/chat/${data[0].id}`, { replace: true });
    });
    chatAPI.users().then(({ data }) => setPeople(data)).catch(() => {});
  }, [loadRooms]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadMessages(activeId); }, [activeId, loadMessages]);

  // Poll for new messages / room updates
  useEffect(() => {
    const t = setInterval(() => {
      loadRooms();
      loadMessages(activeIdRef.current);
    }, POLL_MS);
    return () => clearInterval(t);
  }, [loadRooms, loadMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const activeRoom = rooms.find((r) => r.id === activeId);

  const send = async (e) => {
    e?.preventDefault();
    if (!text.trim() || !activeId) return;
    setSending(true);
    setError('');
    try {
      await chatAPI.send(activeId, text.trim());
      setText('');
      await loadMessages(activeId);
    } catch (err) {
      setError(parseApiError(err, 'Failed to send message'));
    } finally { setSending(false); }
  };

  const upload = async (file) => {
    if (!file || !activeId) return;
    if (file.size > 20 * 1024 * 1024) {
      setError('File exceeds the 20 MB limit');
      return;
    }
    setSending(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      await chatAPI.sendAttachment(activeId, fd);
      await loadMessages(activeId);
    } catch (err) {
      setError(parseApiError(err, 'Upload failed'));
    } finally { setSending(false); }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    upload(f);
  };

  const toggleRecording = async () => {
    if (recording) {
      recorderRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (ev) => chunks.push(ev.data);
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: 'audio/webm' });
        upload(new File([blob], `voice-${Date.now()}.webm`, { type: 'audio/webm' }));
        setRecording(false);
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      setError('Microphone access was denied');
    }
  };

  const removeMessage = async (m) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await chatAPI.deleteMessage(m.id);
      await loadMessages(activeId);
    } catch (err) {
      setError(parseApiError(err, 'Failed to delete message'));
    }
  };

  const openDirect = async (uid) => {
    try {
      const { data } = await chatAPI.openDirect(uid);
      await loadRooms();
      openRoom(data.id);
    } catch (err) {
      setError(parseApiError(err, 'Could not open chat'));
    }
  };

  const createGroup = async (e) => {
    e.preventDefault();
    if (!groupName.trim()) return;
    try {
      const { data } = await chatAPI.createGroup({ name: groupName.trim(), member_ids: groupMembers });
      setShowNewGroup(false); setGroupName(''); setGroupMembers([]);
      await loadRooms();
      openRoom(data.id);
    } catch (err) {
      setError(parseApiError(err, 'Could not create group'));
    }
  };

  const canDelete = (m) => !m.is_deleted && (m.sender_id === user?.id || isAdmin);
  const dmUserIds = new Set(
    rooms.filter((r) => r.type === 'direct').map((r) => r.name)
  );

  return (
    <div className="page">
      <div className="page-header">
        <h2>Chat</h2>
        {isAdmin && (
          <button className="btn btn-primary" onClick={() => setShowNewGroup(!showNewGroup)}>
            {showNewGroup ? 'Cancel' : '+ New Group'}
          </button>
        )}
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {showNewGroup && (
        <form className="form-card mb-4" onSubmit={createGroup}>
          <div className="form-group">
            <label>Group Name *</label>
            <input value={groupName} onChange={(e) => setGroupName(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Members</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {people.filter((p) => p.id !== user?.id).map((p) => (
                <label key={p.id} className="checkbox-label">
                  <input type="checkbox" checked={groupMembers.includes(p.id)}
                    onChange={() => setGroupMembers((prev) =>
                      prev.includes(p.id) ? prev.filter((x) => x !== p.id) : [...prev, p.id])} />
                  {p.full_name}
                </label>
              ))}
            </div>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary">Create Group</button>
          </div>
        </form>
      )}

      <div className="chat-layout">
        {/* Rooms + people */}
        <aside className="chat-sidebar">
          <div className="chat-section-title">Chats</div>
          {rooms.map((r) => (
            <button key={r.id} className={`chat-room${r.id === activeId ? ' active' : ''}`}
              onClick={() => openRoom(r.id)}>
              <div className="chat-room-top">
                <span className="chat-room-name">
                  {r.type === 'group' ? '👥 ' : '💬 '}{r.name}
                </span>
                {r.unread_count > 0 && <span className="chat-unread">{r.unread_count}</span>}
              </div>
              {r.last_message && <div className="chat-room-preview">{r.last_message}</div>}
            </button>
          ))}

          <div className="chat-section-title mt-4">Start a direct chat</div>
          <input className="chat-people-search" placeholder="Search people…"
            value={peopleSearch} onChange={(e) => setPeopleSearch(e.target.value)} />
          {people
            .filter((p) => !dmUserIds.has(p.full_name))
            .filter((p) => p.full_name.toLowerCase().includes(peopleSearch.toLowerCase()))
            .map((p) => (
              <button key={p.id} className="chat-room" onClick={() => openDirect(p.id)}>
                <span className="chat-room-name">＋ {p.full_name}
                  <span className="text-muted" style={{ fontSize: 11 }}> · {p.role.replace('_', ' ')}</span>
                </span>
              </button>
            ))}
          {people.filter((p) => !dmUserIds.has(p.full_name)).length === 0 && (
            <div className="text-muted" style={{ fontSize: 12, padding: '4px 8px' }}>No other users</div>
          )}
        </aside>

        {/* Messages */}
        <section className="chat-main">
          <div className="chat-header">
            <strong>{activeRoom?.name || 'Select a chat'}</strong>
            {activeRoom?.type === 'group' && (
              <span className="text-muted" style={{ fontSize: 12 }}> · {activeRoom.member_count} members</span>
            )}
          </div>

          <div className="chat-messages">
            {messages.length === 0 ? (
              <div className="empty-state">No messages yet. Say hello 👋</div>
            ) : messages.map((m) => {
              const mine = m.sender_id === user?.id;
              return (
                <div key={m.id} className={`chat-msg${mine ? ' mine' : ''}`}>
                  <div className="chat-bubble">
                    {!mine && <div className="chat-sender">{m.sender?.full_name}</div>}

                    {m.is_deleted ? (
                      <div className="chat-deleted">
                        🚫 Message deleted{m.deleted_by_name ? ` by ${m.deleted_by_name}` : ''}
                      </div>
                    ) : (
                      <>
                        {m.type !== 'text' && <Attachment m={m} />}
                        {m.content && <div className="chat-text">{m.content}</div>}
                      </>
                    )}

                    <div className="chat-meta">
                      {!m.is_deleted && <SeenBy seen={m.seen_by} />}
                      <span className="chat-time">{fmtDate(m.created_at)}</span>
                      {canDelete(m) && (
                        <button className="chat-delete" onClick={() => removeMessage(m)} title="Delete message">🗑</button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>

          {activeId && (
            <form className="chat-composer" onSubmit={send}>
              <input ref={fileRef} type="file" style={{ display: 'none' }} onChange={onPickFile}
                accept=".png,.jpg,.jpeg,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.mp3,.wav,.ogg,.webm,.m4a" />
              <button type="button" className="btn btn-ghost" title="Attach file (max 20 MB)"
                onClick={() => fileRef.current.click()} disabled={sending}>📎</button>
              <button type="button" className={`btn ${recording ? 'btn-danger' : 'btn-ghost'}`}
                title={recording ? 'Stop recording' : 'Record voice note'}
                onClick={toggleRecording} disabled={sending}>{recording ? '⏹' : '🎤'}</button>
              <input className="chat-input" placeholder="Type a message…" value={text}
                onChange={(e) => setText(e.target.value)} disabled={sending} />
              <button type="submit" className="btn btn-primary" disabled={sending || !text.trim()}>Send</button>
            </form>
          )}
        </section>
      </div>
    </div>
  );
}
